import { useQueryClient } from '@tanstack/react-query'
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { CalendarIcon, Settings } from 'lucide-react'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { adminRecordInspector } from 'rails_routes'

import { cn, convertToUTCDate, getDateString, getFormattedDate, snakeToSentenceCase } from '../utils'
import CreateEnrollmentEventSheet from './CreateEnrollmentEventSheet'
import { useGetAdminEnrollmentEvent } from '../utils/web_api'
import {
  type AdminEnrollmentEvent,
  type AdminEnrollmentEventEnrollmentSubtype,
  type AdminEnrollmentEventEnrollmentType,
  type PutAdminEnrollmentEventEnrollmentEventId,
} from '../utils/web_api.schemas'
import { useGetEnrollmentEventOptions } from './hooks/enrollments/useGetEnrollmentEventOptions'
import { useUpdateEnrollmentEvent } from './hooks/enrollments/useUpdateEnrollmentEvent'
import Badge from './reusable/Badge'
import ButtonPrimary from './reusable/ButtonPrimary'
import TanstackTable from './reusable/table/TanstackTable'
import Tooltip from './reusable/Tooltip'
import { Button } from './ui/button'
import { Calendar } from './ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Toggle } from './ui/toggle'

type EnrollmentEventsProps = {
  employee_id: string
}

const ENROLLMENT_TYPE_LABELS: Record<AdminEnrollmentEventEnrollmentType, string> = {
  ichra_invite: 'ICHRA Invite',
  open_enrollment: 'Open Enrollment',
  special_enrollment: 'Special Enrollment',
}

const ENROLLMENT_SUBTYPE_LABELS: Record<string, string> = {
  none_selected: 'None Selected',
  renewal: 'Renewal',
  loss_of_coverage: 'Loss of Coverage',
  relocation: 'Relocation',
  birth_or_adoption: 'Birth or Adoption',
  marriage: 'Marriage',
  divorce_or_legal_separation: 'Divorce or Legal Separation',
  death_of_dependent: 'Death of Dependent',
  cobra_expiration: 'COBRA Expiration',
  loss_of_subsidy_eligibility: 'Loss of Subsidy Eligibility',
  thatch_incident: 'Thatch Incident',
  other: 'Other',
}

const getEnrollmentSubtypeLabel = (subtype: AdminEnrollmentEventEnrollmentSubtype | string | null | undefined): string => {
  if (!subtype) return ''
  return ENROLLMENT_SUBTYPE_LABELS[subtype] ?? snakeToSentenceCase(subtype)
}

const EMPTY_SELECT_VALUE = 'none_selected'

const EnrollmentEventsContext = createContext<{
  eventActiveInPlanYear: { [key: number]: boolean }
  isEditing: boolean
  updateEnrollmentEvent: (
  id: string,
  updates: Omit<PutAdminEnrollmentEventEnrollmentEventId, 'employee_id'>
  ) => void
  enrollmentSubtypeOptions: Record<AdminEnrollmentEventEnrollmentType, (string | null)[]>
  employeeId: string
}>({
      eventActiveInPlanYear: {},
      isEditing: false,
      updateEnrollmentEvent: () => {},
      enrollmentSubtypeOptions: {
        ichra_invite: [],
        open_enrollment: [],
        special_enrollment: [],
      },
      employeeId: '',
    })

const columnHelper = createColumnHelper<AdminEnrollmentEvent>()

const CannotActivateTooltip = ({ children }: { children: React.ReactNode }) => {
  return (
    <Tooltip triggerChildren={children}>
      <div
        style={{ zIndex: 100, width: '12rem' }}
        className='text-xs p-2 w-60 rounded-md text-center'
      >
        Cannot activate multiple events in the same plan year
      </div>
    </Tooltip>
  )
}

const createColumns = () => [
  columnHelper.accessor('id', {
    id: 'id',
    cell: (info) => (
      <a
        className='text-sm font-normal'
        href={adminRecordInspector.show({ id: info.getValue() })}
        target='_blank'
        rel='noreferrer'
      >
        {info.getValue().slice(-5)}
      </a>
    ),
    header: () => <div className='text-sm font-normal'>ID</div>,
  }),
  columnHelper.accessor('enrollment_type', {
    id: 'enrollment_type',
    cell: ({ row, getValue }) => {
      const { isEditing, updateEnrollmentEvent, enrollmentSubtypeOptions } = useContext(EnrollmentEventsContext)
      const value = getValue()

      if (!isEditing) {
        return (
          <div className='text-sm font-normal'>{ENROLLMENT_TYPE_LABELS[value]}</div>
        )
      }

      return (
        <Select
          value={value}
          onValueChange={(newValue: AdminEnrollmentEventEnrollmentType) => {
            const currentSubtype = row.original.enrollment_subtype
            const validSubtypes = enrollmentSubtypeOptions[newValue] || []
            const isValidSubtype = currentSubtype != null && validSubtypes.includes(currentSubtype)

            const updates: Omit<PutAdminEnrollmentEventEnrollmentEventId, 'employee_id'> = {
              enrollment_type: newValue,
              ...(!isValidSubtype && { enrollment_subtype: validSubtypes[0] ?? '' }),
            }

            updateEnrollmentEvent(row.original.id, updates)
          }}
        >
          <SelectTrigger className='w-[180px]'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(ENROLLMENT_TYPE_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    },
    header: () => <div className='text-sm font-normal'>Type</div>,
  }),
  columnHelper.accessor('enrollment_subtype', {
    id: 'enrollment_subtype',
    cell: ({ row, getValue }) => {
      const { isEditing, updateEnrollmentEvent, enrollmentSubtypeOptions } = useContext(
        EnrollmentEventsContext
      )
      const value = getValue()
      const enrollmentType = row.original.enrollment_type
      const validSubtypes = enrollmentSubtypeOptions[enrollmentType] || []
      const hasNullOption = validSubtypes.includes(null)

      if (!isEditing) {
        return (
          <div className='text-sm font-normal'>
            {value ? getEnrollmentSubtypeLabel(value) : ''}
          </div>
        )
      }

      return (
        <Select
          value={value || EMPTY_SELECT_VALUE}
          onValueChange={(newValue: string) => {
            updateEnrollmentEvent(row.original.id, {
              enrollment_subtype: newValue === EMPTY_SELECT_VALUE ? '' : newValue,
            })
          }}
        >
          <SelectTrigger className='w-[180px]'>
            <SelectValue placeholder='None Selected' />
          </SelectTrigger>
          <SelectContent>
            {hasNullOption && (
              <SelectItem value={EMPTY_SELECT_VALUE}>
                None Selected
              </SelectItem>
            )}
            {validSubtypes.filter((subtype) => subtype !== null).map((subtype) => (
              <SelectItem key={subtype} value={subtype}>
                {ENROLLMENT_SUBTYPE_LABELS[subtype] ?? getEnrollmentSubtypeLabel(subtype)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )
    },
    header: () => <div className='text-sm font-normal'>Subtype</div>,
  }),
  columnHelper.accessor('plan_year', {
    id: 'plan_year',
    cell: (info) => (
      <div className='text-sm font-normal'>{info.getValue()}</div>
    ),
    header: () => <div className='text-sm font-normal'>Plan year</div>,
  }),
  columnHelper.accessor('status', {
    id: 'status',
    cell: (info) => {
      switch (info.getValue()) {
        case 'inactive':
          return <Badge text='Inactive' type='default' />
        case 'completed':
          return <Badge text='Completed' type='success' />
        case 'in_progress':
          return <Badge text='In Progress' type='highlight' />
        case 'active':
          return <Badge text='Active' type='attention' />
        default:
          return <div className='text-sm font-normal'>NA</div>
      }
    },
    header: () => <div className='text-sm font-normal'>Status</div>,
  }),
  columnHelper.accessor('enrollment_window_start_date', {
    id: 'enrollment_window_start_date',
    cell: ({ row, getValue }) => {
      const { isEditing, updateEnrollmentEvent } = useContext(
        EnrollmentEventsContext
      )

      if (!isEditing) {
        return (
          <div className='text-sm font-normal'>
            {getFormattedDate(getValue(), { timeZone: 'UTC' })}
          </div>
        )
      }

      if (row.original.status === 'active') {
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                className={cn(
                  'w-auto pl-3 text-left font-normal rounded-md px-2 text-xs',
                  !getValue() && 'text-muted-foreground'
                )}
              >
                {getValue()
                  ? (
                      getFormattedDate(getValue(), { timeZone: 'UTC' })
                    )
                  : (
                    <span>Pick a date</span>
                    )}
                <CalendarIcon className='pl-1 ml-auto h-4 w-4 opacity-50' />
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='start'>
              <Calendar
                mode='single'
                selected={convertToUTCDate(new Date(getValue()))}
                onSelect={(date) => {
                  if (!date) return
                  updateEnrollmentEvent(row.original.id, {
                    enrollment_window_start_date: getDateString(date),
                  })
                }}
                disabled={false}
                autoFocus
                defaultMonth={new Date(getValue())}
              />
            </PopoverContent>
          </Popover>
        )
      }

      return <div className='text-sm font-normal'>{getValue()}</div>
    },
    header: () => <div className='text-sm font-normal'>Start date</div>,
  }),
  columnHelper.accessor('enrollment_window_end_date', {
    id: 'enrollment_window_end_date',
    cell: ({ row, getValue }) => {
      const { isEditing, updateEnrollmentEvent } = useContext(
        EnrollmentEventsContext
      )

      if (!isEditing) {
        return (
          <div className='text-sm font-normal'>
            {getFormattedDate(getValue(), { timeZone: 'UTC' })}
          </div>
        )
      }

      if (row.original.status === 'active') {
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                className={cn(
                  'w-auto pl-3 text-left font-normal rounded-md px-2 text-xs',
                  !getValue() && 'text-muted-foreground'
                )}
              >
                {getValue()
                  ? (
                      getFormattedDate(getValue(), { timeZone: 'UTC' })
                    )
                  : (
                    <span>Pick a date</span>
                    )}
                <CalendarIcon className='pl-1 ml-auto h-4 w-4 opacity-50' />
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='start'>
              <Calendar
                mode='single'
                selected={convertToUTCDate(new Date(getValue()))}
                onSelect={(date) => {
                  if (!date) return
                  updateEnrollmentEvent(row.original.id, {
                    enrollment_window_end_date: getDateString(date),
                  })
                }}
                disabled={false}
                autoFocus
                defaultMonth={new Date(getValue())}
              />
            </PopoverContent>
          </Popover>
        )
      }

      return <div className='text-sm font-normal'>{getValue()}</div>
    },
    header: () => <div className='text-sm font-normal'>End date</div>,
  }),
  columnHelper.display({
    id: 'actions',
    cell: ({ row }) => {
      const { eventActiveInPlanYear, isEditing, updateEnrollmentEvent } =
        useContext(EnrollmentEventsContext)
      const { plan_year: planYear, status } = row.original

      if (!isEditing) {
        return <div className='text-sm font-normal'>NA</div>
      }

      // Do not allow the activation of past year's events.
      if (row.original.plan_year < new Date().getFullYear()) {
        return <div className='text-sm font-normal'>NA</div>
      }

      if (status === 'active') {
        return (
          <ButtonPrimary
            onClick={() => {
              updateEnrollmentEvent(row.original.id, {
                status: 'inactive',
              })
            }}
            styles={{ width: '6rem', height: 'auto', padding: '6px' }}
          >
            Deactivate
          </ButtonPrimary>
        )
      }

      if (eventActiveInPlanYear[planYear]) {
        // Add Tooltip saying you can't activate this event because another event is active in this plan year.
        return (
          <CannotActivateTooltip>
            <ButtonPrimary
              styles={{ width: '6rem', height: 'auto', padding: '6px' }}
              isDisabled
              onClick={() => {}}
            >
              Activate
            </ButtonPrimary>
          </CannotActivateTooltip>
        )
      }

      return (
        <ButtonPrimary
          onClick={() => {
            updateEnrollmentEvent(row.original.id, {
              status: 'active',
            })
          }}
          styles={{ width: '6rem', height: 'auto', padding: '6px' }}
        >
          Activate
        </ButtonPrimary>
      )
    },
    header: () => <div className='text-sm font-normal'>Action</div>,
  }),
]

function EnrollmentEvents ({ employee_id: employeeId }: EnrollmentEventsProps) {
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [showCreateEvent, setShowCreateEvent] = useState(false)
  const queryClient = useQueryClient()

  const { data: enrollmentEventsData, isLoading, error } = useGetAdminEnrollmentEvent(
    { employee_id: employeeId },
    {
      query: {
        enabled: !!employeeId,
      },
    }
  )

  useEffect(() => {
    if (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to load enrollment events'
      )
    }
  }, [error])

  const { data: enrollmentEventOptions } = useGetEnrollmentEventOptions()

  const { updateEnrollmentEvent } = useUpdateEnrollmentEvent(employeeId)

  const enrollmentSubtypeOptions = useMemo(() => {
    if (!enrollmentEventOptions?.enrollment_types) {
      return {
        ichra_invite: [],
        open_enrollment: [],
        special_enrollment: [],
      }
    }
    return enrollmentEventOptions.enrollment_types
  }, [enrollmentEventOptions])

  const data = enrollmentEventsData?.data || []

  const table = useReactTable({
    data,
    columns: createColumns(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    autoResetAll: false,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 5,
      },
      sorting: [
        { id: 'plan_year', desc: true },
        { id: 'enrollment_window_start_date', desc: true },
      ],
    },
  })

  const eventActiveInPlanYear = useMemo(
    () =>
      data.reduce<{ [key: number]: boolean }>(
        (acc, { plan_year: planYear, status }) => {
          if (status === 'active') {
            acc[planYear] = true
          }
          return acc
        },
        {}
      ),
    [data]
  )

  return (
    <EnrollmentEventsContext.Provider
      value={{
        eventActiveInPlanYear,
        isEditing,
        updateEnrollmentEvent,
        enrollmentSubtypeOptions,
        employeeId,
      }}
    >
      <div className='mt-4 flex justify-between items-center'>
        <Toggle
          variant='outline'
          onPressedChange={setIsEditing}
          pressed={isEditing}
        >
          <Settings />
          <span className='ml-2'>Edit Events</span>
        </Toggle>
        <Button onClick={() => setShowCreateEvent(true)}>Create Event</Button>
      </div>
      <div className='mt-4'>
        <TanstackTable
          table={table}
          isLoading={isLoading}
          paginationLabelSingular='enrollment event'
        />
      </div>
      <CreateEnrollmentEventSheet
        employeeId={employeeId}
        isOpen={showCreateEvent}
        onClose={() => setShowCreateEvent(false)}
        onEventCreated={() => {
          queryClient.invalidateQueries({
            queryKey: ['/api/web/admin/enrollment_event'],
          })
        }}
      />
    </EnrollmentEventsContext.Provider>
  )
}

export default EnrollmentEvents
